import { Text } from 'blaze-ui/Text/Text'
import Styles from './FontItem.module.scss'
import { Edit1 } from 'blaze-ui/icons'
import { UploadCloud } from 'almanac-ui/icons'
import { Pressable } from '@react-aria/interactions'
import { FontRole } from 'types'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { useRootStore } from 'stores/RootStore'
import { findClosestWeight } from 'flows/common/steps/BrandKitColorsAndFonts/BrandKitFontPicker/BrandKitFontPicker'
import { observer } from 'mobx-react-lite'
import { Skeleton } from 'antd'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { useModals } from 'almanac-ui/Modal'
import CustomizeFontModal from '../CustomizeFontModal/CustomizeFontModal'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { EditDrawer } from './FontPicker/EditDrawer/EditDrawer'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { CustomFont } from 'stores/CustomFonts/CustomFont'
import { FontsUploadModal } from 'components/FontsUploadModal/FontsUploadModal'

interface Props {
  role: FontRole.Title | FontRole.Body
  fontFamily: string
  fontWeight?: FontWeightType
  fontStyle?: string
  onChange: (role: FontRole, family: string, weights: FontWeightType[], style: string) => void
}

export const FontItem = observer(({ role, fontFamily, fontWeight, fontStyle, onChange }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { openModal } = useModals()
  const { fonts, drawersStore } = useRootStore()

  const font = fonts.get(fontFamily)

  const getDefaultWeight = () => (role === FontRole.Title ? 700 : 400)
  const isReady = font?.loadingState === 'loaded' || font instanceof CustomFont

  const weight =
    fontWeight ??
    ((font?.weights?.length ?? 0) > 0 ? findClosestWeight(font?.weights ?? [], getDefaultWeight()) : getDefaultWeight())

  const handleEdit = () => {
    if (isMobile) {
      drawersStore.openDrawer(EditDrawer, {
        role,
        fontFamily,
        fontWeight: fontWeight ?? weight,
        fontStyle: fontStyle ?? 'normal',
        onChange,
        onClose: () => {
          drawersStore.closeDrawer()
        },
      })
    } else {
      openModal(CustomizeFontModal, {
        onSave: (role: FontRole, family: string, weight: FontWeightType, style: string) => {
          onChange(role, family, [weight], style)
        },
        role,
        initialFontFamily: fontFamily,
        initialFontWeight: fontWeight ?? weight,
        initialFontStyle: fontStyle ?? 'normal',
      })
    }
  }

  const handleUploadFont = () => {
    openModal(FontsUploadModal)
  }

  return (
    <>
      <ResponsiveDesign.Desktop>
        <div className={Styles.fontSample}>
          <Text variant="smallList" color="var(--dark-40)">
            {role === FontRole.Title ? 'Title' : 'Body'}
          </Text>
          <span className={Styles.titleFont} style={{ fontFamily: `'${fontFamily}'`, fontWeight: weight, fontStyle }}>
            <span className={Styles.fontNameText}>
              {isReady ? fontFamily : <Skeleton paragraph={false} active className={Styles.fontSkeleton} />}
            </span>
          </span>
          {isReady && (
            <div className={Styles.buttonContainer}>
              <button className={Styles.customButton} onClick={handleEdit}>
                <Edit1 size={20} color="var(--selected-fill, #0099F7)" />
                <span className={Styles.buttonText}>Change</span>
              </button>
              <button className={Styles.customButton} onClick={handleUploadFont}>
                <UploadCloud size={20} color="var(--selected-fill, #0099F7)" />
                <span className={Styles.buttonText}>Upload Your Own Font</span>
              </button>
            </div>
          )}
        </div>
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <Pressable onPress={handleEdit}>
          <div className={Styles.fontSample}>
            <Text variant="smallList" color="var(--dark-40)">
              {role === FontRole.Title ? 'Title' : 'Body'}
            </Text>
            <span className={Styles.titleFont} style={{ fontFamily: `'${fontFamily}'`, fontWeight: weight, fontStyle }}>
              <span className={Styles.fontNameText}>
                {isReady ? fontFamily : <Skeleton paragraph={false} active className={Styles.fontSkeleton} />}
              </span>
            </span>
          </div>
        </Pressable>
      </ResponsiveDesign.Mobile>
      {fontFamily && (
        <>
          <GoogleFontLoader family={fontFamily} />
          <BlazeFontLoader name={fontFamily} />
        </>
      )}
    </>
  )
})
